import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import {
  CounterComponent,
  CounterStyle
} from '../../../../../../shared/components/controllers/counter/counter.component';
import { FormattedCurrencyPipe } from '../../../../../../shared/pipes/formatted-currency.pipe';
import { FormsModule } from '@angular/forms';
import { WooCommerceCartItemAddOn } from '../../../../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import { KeyValuePipe } from '@angular/common';

interface ExpandedWooCommerceCartItemAddOn extends WooCommerceCartItemAddOn {
  price: number;
}

interface GroupedAddOnItem
  extends Omit<ExpandedWooCommerceCartItemAddOn, 'name'> {
  price: number;
}

type GroupedAddOns = Record<string, GroupedAddOnItem[]>;

@Component({
  selector: 'app-checkout-details-cart-item',
  imports: [CounterComponent, FormattedCurrencyPipe, FormsModule, KeyValuePipe],
  templateUrl: './checkout-details-cart-item.component.html',
  styleUrl: './checkout-details-cart-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutDetailsCartItemComponent {
  protected readonly CounterStyle = CounterStyle;

  //TODO:Chef Salad
  name: InputSignal<string> = input<string>('');
  image: InputSignal<string> = input<string>('');
  price: InputSignal<string> = input<string>('');
  addOns: InputSignal<WooCommerceCartItemAddOn[]> = input<
    WooCommerceCartItemAddOn[]
  >([]);

  // State
  isDisabled: InputSignal<boolean> = input<boolean>(false);

  // Quantity
  quantity: ModelSignal<number> = model<number>(0);
  minQuantity: InputSignal<number> = input<number>(0);
  maxQuantity: InputSignal<number> = input<number>(0);

  cartMinorUnit: InputSignal<number> = input<number>(2);

  remove: OutputEmitterRef<void> = output<void>();
  update: OutputEmitterRef<number> = output<number>();

  groupedAddOns: Signal<GroupedAddOns> = computed((): GroupedAddOns => {
    const addOns: WooCommerceCartItemAddOn[] = this.addOns();
    return this.groupByName(addOns);
  });

  onRemove() {
    this.remove.emit();
  }

  onUpdate(quantity: number) {
    this.update.emit(quantity);
  }

  private expandAddOn(
    item: WooCommerceCartItemAddOn
  ): ExpandedWooCommerceCartItemAddOn {
    if (item.price != null && item.price !== '') {
      const numericPrice = Number(item.price);
      return {
        ...item,
        price: Number.isFinite(numericPrice) ? numericPrice : 0
      };
    }

    const match: RegExpMatchArray | null = item.name.match(/&#36;(\d+\.\d{2})/);
    const price: number | null = match ? parseFloat(match[1]) : null;
    const cleanedName: string = item.name.replace(/\s*\(&#36;\d+\.\d{2}\)/, '');

    return { ...item, name: cleanedName, price: price || 0 };
  }

  private groupByName(rawItems: WooCommerceCartItemAddOn[]): GroupedAddOns {
    const grouped: GroupedAddOns = {};
    const items = rawItems
      .filter((rawItem) => {
        const hasName = !!rawItem?.name && String(rawItem.name).trim() !== '';
        const hasValue = !!rawItem?.value && String(rawItem.value).trim() !== '';
        return hasName || hasValue;
      })
      .map((rawItem) => this.expandAddOn(rawItem));

    for (const item of items) {
      const { name, ...rest } = item;
      const groupKey = name || 'Options';

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }

      grouped[groupKey].push(rest);
    }

    return grouped;
  }
}
